import type { DocumentNode } from '@apollo/client';
import { gql, useQuery } from '@apollo/client';
import { CircularProgress, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import type { Updater } from '@tanstack/react-table';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type { MRT_PaginationState } from 'material-react-table';
import type { ReactElement } from 'react';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { jobsQuery } from 'src/dynamoQueries/jobs';
import type { Job } from 'src/Jobs/JobsTable';
import JobsTable from 'src/Jobs/JobsTable';

interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
}

interface JobEdge {
  node: Job;
}

interface AllJobsData {
  edges: JobEdge[];
  totalCount: number;
  pageInfo: PageInfo;
}

interface JobsQueryResponse {
  allJobs: AllJobsData;
}

interface JobsQueryVariables {
  first?: number | null;
  last?: number | null;
  before?: string | null;
  after?: string | null;
}

const JobsContent = (): ReactElement => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageSize: 10, pageIndex: 0 });
  const [totalCount, setTotalCount] = useState<number>(0);
  const [jobs, setJobs] = useState<Job[]>([]);

  const buildJobsQuery = (): DocumentNode => {
    const q = jobsQuery();
    const gQuery = jsonToGraphQLQuery(q, { pretty: true });
    return gql(gQuery);
  };

  const {
    loading: jobsQueryLoading,
    data: jobsData,
    fetchMore,
  } = useQuery<JobsQueryResponse, JobsQueryVariables>(buildJobsQuery(), {
    variables: {
      first: pagination.pageSize,
    },
    onCompleted: (data: JobsQueryResponse) => {
      setJobs(data.allJobs?.edges?.map((edge: JobEdge) => edge.node) ?? []);
      setTotalCount(data.allJobs?.totalCount ?? 0);
    },
    fetchPolicy: 'network-only',
  });

  /**
   * https://www.material-react-table.com/docs/guides/pagination#manage-pagination-state
   * @param updater - pagination state update function or new state value
   */
  const updatePagination = (updater: Updater<MRT_PaginationState>): void => {
    const newState = typeof updater === 'function' ? updater(pagination) : updater;
    const pageInfo = jobsData?.allJobs?.pageInfo;

    if (!pageInfo) {
      return;
    }

    if (newState.pageSize !== pagination.pageSize) {
      // increase page size
      newState.pageIndex = 0; // reset the page index with new page size
      fetchMore({
        variables: {
          first: newState.pageSize,
          after: null,
        },
      });
    } else if (newState.pageIndex > pagination.pageIndex) {
      // page forwards
      fetchMore({
        variables: {
          first: newState.pageSize,
          after: pageInfo.endCursor,
          last: null,
          before: null,
        },
        updateQuery: (prev: JobsQueryResponse, { fetchMoreResult }: { fetchMoreResult?: JobsQueryResponse }) => {
          if (!fetchMoreResult) return prev;
          return fetchMoreResult;
        },
      });
    } else if (newState.pageIndex < pagination.pageIndex) {
      // page backwards
      fetchMore({
        variables: {
          last: newState.pageSize,
          before: pageInfo.startCursor,
          first: null,
          after: null,
        },
        updateQuery: (prev: JobsQueryResponse, { fetchMoreResult }: { fetchMoreResult?: JobsQueryResponse }) => {
          if (!fetchMoreResult) return prev;
          return fetchMoreResult;
        },
      });
    }
    setPagination(newState);
  };

  if (jobsQueryLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height={'75vh'} width={'100%'}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (jobs && jobs.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height={'75vh'} width={'100%'}>
        <Typography variant="h2" sx={{ color: 'error.main' }}>
          No jobs to show
        </Typography>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Jobs</title>
      </Helmet>
      <Box width="90vw" height="90vh">
        <JobsTable
          title="Jobs"
          jobs={jobs}
          pagination={pagination}
          onPaginationChange={updatePagination}
          totalCount={totalCount}
        />
      </Box>
    </React.Fragment>
  );
};

export default JobsContent;
